'use client';

// Social icon row rendered by the header/footer `showSocial` theme toggles.
// Content comes from Company Contact settings; the theme only decides
// whether and how it shows.
import { AtSign, Facebook, Github, Globe, Instagram, Linkedin, MessageCircle, Send, Twitter, Youtube } from 'lucide-react';
import type { CompanyContactSocial } from '@/types/admin';
import { cn } from '@/lib/utils';
import { useContentText, useTranslations } from '@/lib/i18n';

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  facebook: Facebook,
  twitter: Twitter,
  x: Twitter,
  instagram: Instagram,
  linkedin: Linkedin,
  youtube: Youtube,
  github: Github,
  telegram: Send,
  whatsapp: MessageCircle,
  email: AtSign,
};

// Match the explicit icon key first, then the URL host (never a substring of the
// whole URL: "x" would otherwise claim any address containing the letter).
function iconFor(social: CompanyContactSocial) {
  const explicit = social.icon?.toLowerCase().trim();
  if (explicit && ICONS[explicit]) return ICONS[explicit];
  let host = '';
  try {
    host = new URL(social.url).hostname.toLowerCase();
  } catch {
    return Globe;
  }
  if (host === 'x.com' || host.endsWith('.x.com') || host.includes('twitter.')) return Twitter;
  const byHost = Object.keys(ICONS).find((k) => k !== 'x' && host.includes(k));
  return byHost ? ICONS[byHost] : Globe;
}

const isSafeHref = (href: string) => /^(https?:\/\/|mailto:)/i.test(href);

interface SocialIconLinksProps {
  socials: CompanyContactSocial[] | undefined;
  className?: string;
  iconClassName?: string;
}

export function SocialIconLinks({ socials, className, iconClassName }: SocialIconLinksProps) {
  const t = useTranslations('components.social-icon-links');
  const contentContact = useContentText('contact');
  const items = (socials ?? []).filter((s) => s.url && isSafeHref(s.url));
  if (items.length === 0) return null;
  return (
    <ul className={cn('flex items-center gap-1', className)} aria-label={t('social-links')}>
      {items.map((social, index) => {
        const Icon = iconFor(social);
        return (
          <li key={`${social.url}-${index}`}>
            <a
              href={social.url}
              target="_blank"
              rel="noreferrer"
              aria-label={contentContact(social.label) || social.url}
              className="inline-flex size-11 items-center justify-center md:size-9 rounded-[var(--pd-header-nav-item-radius,var(--radius))] text-muted-foreground transition-colors hover:bg-accent hover:text-[color:var(--pd-icon-hover-color,hsl(var(--primary)))]"
            >
              <Icon className={cn('size-[var(--pd-icon-size-sm,1rem)]', iconClassName)} />
            </a>
          </li>
        );
      })}
    </ul>
  );
}
